"use client";

import { useCallback, useRef, useState } from "react";

import { AddressSuggestionList } from "@/components/address/address-suggestion-list";
import { useAddressSuggestionListKeyboard } from "@/components/address/use-address-suggestion-list-keyboard";
import { CancelSmallIcon, SearchIcon } from "@/components/icons-lib";
import type { IGeocodeAddressPublicItem } from "@/providers/store/api/find-my-school/find-my-school.interface";
import { cn } from "@/shared/helpers/cn.helper";

export interface AddressSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: IGeocodeAddressPublicItem[];
  onSearch: (address: string) => void;
  onSelect: (item: IGeocodeAddressPublicItem) => void;
  onClear?: () => void;
  placeholder?: string;
  isLoading?: boolean;
  showNoResults?: boolean;
  className?: string;
  variant?: "default" | "mobile";
}

function getInputRightPaddingClass(
  mobile: boolean,
  showClear: boolean,
  isLoading: boolean,
): string {
  if (mobile) {
    if (showClear && isLoading) return "pr-20";
    if (showClear || isLoading) return "pr-12";
    return "pr-4";
  }
  if (showClear && isLoading) return "pr-24";
  if (showClear || isLoading) return "pr-20";
  return "pr-4";
}

export function AddressSearchInput({
  value,
  onChange,
  suggestions,
  onSearch,
  onSelect,
  onClear,
  placeholder = "Enter your address",
  isLoading = false,
  showNoResults = false,
  className,
  variant = "default",
}: AddressSearchInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSelect = useCallback(
    (item: {
      latitude: number;
      longitude: number;
      formattedAddress?: string;
    }) => {
      if (blurTimeoutRef.current) {
        window.clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      onSelect({
        latitude: item.latitude,
        longitude: item.longitude,
        formattedAddress: item.formattedAddress ?? "",
      });
      setIsFocused(false);
    },
    [onSelect],
  );

  const keyboard = useAddressSuggestionListKeyboard({
    enabled: isFocused,
    items: suggestions,
    onSelect: handleSelect,
  });

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && keyboard.isOpen && keyboard.highlightIndex < 0) {
        e.preventDefault();
        if (value.trim()) onSearch(value.trim());
        return;
      }
      keyboard.onKeyDown(e);
      if (e.defaultPrevented) {
        return;
      }
      if (e.key === "Enter" && value.trim()) {
        onSearch(value.trim());
      }
    },
    [keyboard, onSearch, value],
  );

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => setIsFocused(false), 200);
  }, []);

  const showSuggestions = keyboard.isOpen;
  const showDropdown = isFocused && (showSuggestions || showNoResults);
  const showClear = Boolean(onClear && value.trim() !== "");
  const mobile = variant === "mobile";
  const inputRightPaddingClass = getInputRightPaddingClass(
    mobile,
    showClear,
    isLoading,
  );

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <SearchIcon
          className={cn(
            "absolute top-1/2 -translate-y-1/2 text-gray-600",
            mobile ? "left-4 h-6 w-6" : "left-3 h-5 w-5",
          )}
        />
        <input
          autoComplete="off"
          className={cn(
            "w-full border border-gray-200 bg-white text-gray-900",
            mobile
              ? "h-12 rounded-full py-0 pl-12 text-[16px]/[20px] shadow-[0_2px_10px_rgba(0,0,0,0.20)]"
              : "rounded-lg py-3 pl-10 text-sm",
            inputRightPaddingClass,
            "focus:border-primary-500 focus:ring-primary-500 placeholder:text-gray-400 focus:outline-none focus:ring-1",
          )}
          placeholder={placeholder}
          type="text"
          value={value}
          onBlur={handleBlur}
          onChange={(e) => {
            onChange(e.target.value);
            if (!isFocused) setIsFocused(true);
          }}
          onFocus={() => {
            if (blurTimeoutRef.current) {
              window.clearTimeout(blurTimeoutRef.current);
              blurTimeoutRef.current = null;
            }
            setIsFocused(true);
          }}
          onKeyDown={handleKeyDown}
        />
        <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {showClear && (
            <button
              aria-label="Clear address"
              className="rounded p-0.5 hover:bg-gray-100"
              type="button"
              onClick={() => onClear?.()}
              onMouseDown={(e) => e.preventDefault()}
            >
              <CancelSmallIcon className="h-4 w-4 text-[#161515]" />
            </button>
          )}
          {isLoading && (
            <div className="border-primary-500 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-t-transparent" />
          )}
        </div>
      </div>

      {showDropdown && (
        <div
          className={cn(
            "absolute z-10 mt-1 w-full overflow-hidden border border-gray-200 bg-white shadow-lg",
            mobile ? "rounded-[14px]" : "rounded-lg",
          )}
        >
          {showSuggestions ? (
            <AddressSuggestionList
              highlightIndex={keyboard.highlightIndex}
              items={suggestions}
              query={value}
              onHighlightChange={keyboard.setHighlightIndex}
              onSelect={handleSelect}
            />
          ) : (
            <div
              className="px-3 py-3 text-sm text-gray-500"
              role="status"
            >
              No results found
            </div>
          )}
        </div>
      )}
    </div>
  );
}
